/* PurgeDialog — superadmin-tool om alle records met een bepaalde pattern te
 * verwijderen (orgs, users, magic-links, sessies + cascade).
 *
 * Flow:
 *   1) User typt pattern (min 3 chars) → live preview via /platform/purge-by-pattern/preview
 *   2) User ziet exact wat weggaat — orgs, users, magic-links, cascade counts, beschermde items
 *   3) User typt pattern opnieuw als confirm → POST /platform/purge-by-pattern
 *
 * Beveiligingslagen (dubbel getypte pattern + backend-guards) voorkomen accidental full-nuke.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, ShieldCheck, Trash2 } from "lucide-react";

export default function PurgeDialog({ open, onOpenChange, onDone }) {
  const [pattern, setPattern] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset bij openen
  useEffect(() => {
    if (open) {
      setPattern("");
      setPreview(null);
      setConfirm("");
      setBusy(false);
    }
  }, [open]);

  // Debounced preview zodra pattern lang genoeg is
  useEffect(() => {
    if (!open) return;
    if (pattern.trim().length < 3) { setPreview(null); return; }
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(
          `/platform/purge-by-pattern/preview?pattern=${encodeURIComponent(pattern.trim())}`,
        );
        setPreview(r.data);
      } catch (e) {
        setPreview(null);
        toast.error(formatApiError(e?.response?.data?.detail || e.message));
      } finally {
        setPreviewing(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [pattern, open]);

  const totals = preview?.totals;
  const isEmpty = totals && Object.values(totals).every(v => v === 0);
  const canPurge = preview && !isEmpty && confirm === pattern.trim() && !busy;

  const doPurge = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        `/platform/purge-by-pattern?pattern=${encodeURIComponent(pattern.trim())}&confirm=${encodeURIComponent(pattern.trim())}`,
      );
      const del = r.data.deleted || {};
      const total = Object.values(del).reduce((a, b) => a + b, 0);
      toast.success(`Purge voltooid — ${total} records verwijderd`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-2xl bg-white" data-testid="purge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" /> KDV opruimen (full nuke)
          </DialogTitle>
          <DialogDescription>
            Verwijder alle records (organisaties, users, magic-links, cascade van tenant-data)
            waarvan de naam, slug of e-mail de opgegeven zoektekst bevat. Demo-tenants en
            platform-admins blijven altijd behouden.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="purge-pattern" className="text-sm font-medium">
              Zoekterm (minimaal 3 tekens)
            </Label>
            <Input
              id="purge-pattern"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="bijv. nannypoppins"
              className="mt-1"
              data-testid="purge-pattern-input"
              disabled={busy}
              autoFocus
            />
          </div>

          {previewing && (
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Preview laden…
            </div>
          )}

          {preview && !previewing && (
            <div data-testid="purge-preview" className="border border-neutral-200 rounded-lg p-4 space-y-3 bg-neutral-50/50">
              {isEmpty ? (
                <div className="text-sm text-neutral-500">
                  Geen matching records gevonden voor <strong>{pattern.trim()}</strong>.
                </div>
              ) : (
                <>
                  <div className="text-sm font-semibold text-neutral-800">
                    Wordt verwijderd:
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <PurgeStat label="Organisaties" n={totals.organizations} data-testid="purge-count-orgs" />
                    <PurgeStat label="Users" n={totals.users} data-testid="purge-count-users" />
                    <PurgeStat label="Magic-links" n={totals.magic_links} data-testid="purge-count-magic" />
                    <PurgeStat label="Onboarding-sessies" n={totals.onboarding_sessions} data-testid="purge-count-sess" />
                    <PurgeStat label="Tenant-records (cascade)" n={totals.tenant_records_cascade} full data-testid="purge-count-cascade" />
                  </div>
                  {preview.would_delete?.organizations?.length > 0 && (
                    <details className="text-xs text-neutral-600">
                      <summary className="cursor-pointer font-medium">Toon organisaties ({preview.would_delete.organizations.length})</summary>
                      <ul className="mt-1 pl-4 list-disc space-y-0.5">
                        {preview.would_delete.organizations.map(o => (
                          <li key={o.id}><strong>{o.name}</strong> — {o.slug} · {o.email || "—"}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {(preview.protected?.users?.length > 0 || preview.protected?.demo_organizations?.length > 0) && (
                    <div className="flex items-start gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                      <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium">Beschermd — blijft behouden</div>
                        <div>
                          {preview.protected.users?.length || 0} platform-user(s),
                          {' '}{preview.protected.demo_organizations?.length || 0} demo-organisatie(s).
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {preview && !isEmpty && (
            <div>
              <Label htmlFor="purge-confirm" className="text-sm font-medium text-red-700">
                Typ <code className="px-1 rounded bg-red-50 border border-red-200">{pattern.trim()}</code> ter bevestiging
              </Label>
              <Input
                id="purge-confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1"
                data-testid="purge-confirm-input"
                disabled={busy}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="purge-cancel-btn">
            Annuleren
          </Button>
          <Button
            variant="destructive"
            onClick={doPurge}
            disabled={!canPurge}
            data-testid="purge-execute-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            {busy ? "Bezig met opruimen…" : "Definitief opruimen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PurgeStat({ label, n, full = false, ...rest }) {
  return (
    <div
      {...rest}
      className={`flex items-center justify-between px-3 py-2 bg-white border border-neutral-200 rounded ${full ? "col-span-2" : ""}`}>
      <span className="text-neutral-600">{label}</span>
      <span className={`font-mono font-semibold ${n > 0 ? "text-red-700" : "text-neutral-400"}`}>{n}</span>
    </div>
  );
}
